import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";
import { useSelector } from "react-redux";
import React from "react";
import SaveIcon from "@mui/icons-material/Save";
import { setDisplayErrorSnackBar } from "../../../../../../../../../features/userSlice";
import CommonDataGridTable from "../../../../../CommonDataGridTable/CommonDataGridTable";
import { useDispatch } from "react-redux";
import { useSearchTermsTab } from "../../../../../STOperationhooks/useSearchTermsTab";
import {
  setConfirmLoaderSTControl,
  setSelectedDefinitionsForUnclassifiedST,
  setSelectedRowsForUnclassifiedST,
} from "../../../../../../../../../features/searchTermTabSlice";

interface DefinitionModalSTProps {
  open: boolean;
  onClose: () => void;
}

const DefinitionModalST: React.FC<DefinitionModalSTProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useDispatch();
  const {
    selectedRowsForUnclassifiedST, // get the selected rows
    selectedDefinitionsForUnclassifiedST, // get the selected definitions with rows
    definitionModalSourceForUnclassifiedST, // get the source of modal open (headerIcon or saveButton)
    definitionModalTypeForUnclassifiedST, // get the modal type (Exclusive, Allowed, Negative)
    confirmLoaderSTControl,
  } = useSelector((state: any) => state.searchTermTab);

  const {
    createPositiveSearchTermLinks,
    createNegativeSearchTermLinks,
    getSTPerformanceUnclassified,
  } = useSearchTermsTab(); //custom hook to handle API calls

  // === COLUMNS ===
  const columns: GridColDef[] = [
    {
      field: "keyword_name",
      headerName: "Keyword Name",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "search_term_match_type",
      headerName: "Match Type",
      flex: 0.6,
      minWidth: 110,
    },
    {
      field: "adgroup_name",
      headerName: "Adgroup Name",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "campaign_name",
      headerName: "Campaign Name",
      flex: 1,
      minWidth: 220,
    },
  ];

  let mergedList = [];

  // === MERGE SELECTED ROWS WITH DEFINITIONS ===
  if (definitionModalSourceForUnclassifiedST === "headerIcon") {
    mergedList = selectedRowsForUnclassifiedST.map((row: any) => ({
      id: row.id,
      search_term: row.search_term,
      keyword_name: row.keyword_name,
      keyword_id: row.keyword_id,
      search_term_match_type: row.search_term_match_type,
      campaign_id: row.campaign_id,
      campaign_name: row.campaign_name,
      adgroup_id: row.adgroup_id,
      adgroup_name: row.adgroup_name,
      ad_shared_uid: row.ad_shared_uid,
      scope_shared_uid: row.scope_shared_uid,
      action: definitionModalTypeForUnclassifiedST || "", // fallback for safety
    }));
  } else if (definitionModalSourceForUnclassifiedST === "saveButton") {
    mergedList = Object.values(selectedDefinitionsForUnclassifiedST)
      .filter((row: any) => {
        // check if action exists and is not empty
        return (
          row?.action &&
          typeof row.action === "string" &&
          row.action.trim() !== ""
        );
      })
      .map((row: any) => ({
        id: row.id,
        search_term: row.search_term,
        keyword_name: row.keyword_name,
        keyword_id: row.keyword_id,
        search_term_match_type: row.search_term_match_type,
        campaign_id: row.campaign_id,
        campaign_name: row.campaign_name,
        adgroup_id: row.adgroup_id,
        adgroup_name: row.adgroup_name,
        ad_shared_uid: row.ad_shared_uid,
        scope_shared_uid: row.scope_shared_uid,
        action: row.action, // action is already part of the row object
      }));
  } else {
    mergedList = [];
  }
  // === GROUP DATA BY ACTION ===
  const groupedData = {
    Exclusive: mergedList.filter((item: any) => item.action === "Exclusive"),
    Allowed: mergedList.filter((item: any) => item.action === "Allowed"),
    Negative: mergedList.filter((item: any) => item.action === "Negative"),
  };

  const renderGrid = (title: string, rows: any[]) => {
    if (rows.length === 0) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography>{title}</Typography>
        <CommonDataGridTable rows={rows} columns={columns} />
      </Box>
    );
  };

  const hasData =
    groupedData.Exclusive.length > 0 ||
    groupedData.Allowed.length > 0 ||
    groupedData.Negative.length > 0;

  // === CLASSIFY SEARCH TERMS(Exclusive + Allowed + Negative) ===
  const classifySearchTermsHandler = async () => {
    dispatch(setConfirmLoaderSTControl(true));
    let totalSuccess = 0;
    let totalFailed = 0;
    try {
      // === Process Exclusive Only ===
      if (groupedData.Exclusive?.length > 0) {
        const exclusiveData = groupedData.Exclusive || [];
        const { successCount, failCount } = await createPositiveSearchTermLinks(
          exclusiveData,
          true, // addNegative
          "Exclusive"
        );
        totalSuccess += successCount;
        totalFailed += failCount;
      }

      // === Process Allowed Only ===
      if (groupedData.Allowed?.length > 0) {
        const allowedData = groupedData.Allowed || [];
        const { successCount, failCount } = await createPositiveSearchTermLinks(
          allowedData, // Send all items
          false, // addNegative
          "Allowed"
        );
        totalSuccess += successCount;
        totalFailed += failCount;
      }

      // === Process Negative ===
      if (groupedData?.Negative?.length > 0) {
        const negativeData = groupedData.Negative || [];
        const reqBody = {
          terms: negativeData.map((item: any) => ({
            term: item.search_term,
            match_type: item.search_term_match_type,
            level: "ad_group",
            level_shared_uid: item.ad_shared_uid,
          })),
        };
        const { successCount, failCount } = await createNegativeSearchTermLinks(
          reqBody
        );
        totalSuccess += successCount;
        totalFailed += failCount;
      }

      // Refresh data + close Modal + claer redux if anything succeeded
      if (totalSuccess > 0) {
        await getSTPerformanceUnclassified();
        dispatch(setSelectedRowsForUnclassifiedST([])); // Clear selected rows
        dispatch(setSelectedDefinitionsForUnclassifiedST({})); // Clear selected definitions
        onClose(); // Close modal
      }

      // if all failed, dont close modal
      if (totalSuccess === 0 && totalFailed > 0) {
        dispatch(
          setDisplayErrorSnackBar({
            message: `All ${totalFailed} operation(s) failed. Please try again.`,
            type: "error",
          })
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to classify search terms.";
      dispatch(
        setDisplayErrorSnackBar({
          message: `Critical error during classification: ${errorMessage}`,
          type: "error",
        })
      );
    } finally {
      dispatch(setConfirmLoaderSTControl(false));
    }
  };
  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Prevent close if loading and the reason is backdrop click or escape key
        if (
          // confirmLoaderUnclassifiedST &&
          reason === "backdropClick" ||
          reason === "escapeKeyDown"
        ) {
          return;
        }
        onClose();
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        Total Number of Adgroups effected: {mergedList.length}
      </DialogTitle>
      <DialogContent>
        {hasData ? (
          <>
            {renderGrid("Set as Exclusive", groupedData.Exclusive)}
            {renderGrid("Set as Allowed", groupedData.Allowed)}
            {renderGrid("Set as Negative", groupedData.Negative)}
          </>
        ) : (
          <Typography
            sx={{ py: 4, textAlign: "center", color: "text.secondary" }}
          >
            No search terms selected.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "16px 20px" }}>
        <Button
          onClick={onClose}
          disabled={confirmLoaderSTControl}
          color="secondary"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          data-tracker="546"
          variant="contained"
          color="secondary"
          loading={confirmLoaderSTControl}
          loadingPosition="end"
          endIcon={confirmLoaderSTControl && <SaveIcon />}
          disabled={!hasData}
          onClick={classifySearchTermsHandler}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DefinitionModalST;
